import { Link } from "react-router-dom";
import { BedDouble, Bath, Car, Ruler, MapPin } from "lucide-react";
import { resolveImage } from "@/lib/api";
import { useSite, STATUS } from "@/context/SiteContext";

export default function PropertyCard({ property, index = 0 }) {
  const { formatPrice } = useSite();
  const p = property;
  const status = STATUS[p.status] || STATUS.disponivel;
  const cover = p.images?.[0];

  return (
    <Link
      to={`/imoveis/${p.id}`}
      className="group block bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1"
      data-testid={`property-card-${p.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {cover ? (
          <img
            src={resolveImage(cover)}
            alt={`Foto de ${p.title}`}
            loading={index > 2 ? "lazy" : "eager"}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">Sem fotografia</div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${status.cls}`} data-testid={`property-status-${p.id}`}>
            {status.label}
          </span>
          {p.is_demo && (
            <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-900/80 text-white" data-testid={`property-demo-badge-${p.id}`}>
              Exemplo
            </span>
          )}
        </div>
        <span className="absolute top-3 right-3 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/90 text-slate-800">
          {p.type === "apartamento" ? "Apartamento" : "Casa"}
        </span>
      </div>
      <div className="p-5 sm:p-6">
        <h3 className="text-lg font-bold text-slate-900 tracking-tight group-hover:text-brand transition-colors duration-200 line-clamp-1">
          {p.title}
        </h3>
        <p className="mt-1 text-sm text-slate-500 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-brand" />
          {[p.neighborhood, p.city].filter(Boolean).join(" · ") || "Localização a confirmar"}
        </p>
        <div className="mt-4 flex items-center gap-4 text-xs text-slate-600 font-medium">
          {p.area_privativa ? (
            <span className="flex items-center gap-1"><Ruler className="w-3.5 h-3.5 text-slate-400" />{p.area_privativa} m²</span>
          ) : null}
          {p.bedrooms ? (
            <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5 text-slate-400" />{p.bedrooms}</span>
          ) : null}
          {p.suites ? (
            <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5 text-slate-400" />{p.suites} suítes</span>
          ) : null}
          {p.parking ? (
            <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5 text-slate-400" />{p.parking}</span>
          ) : null}
        </div>
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
          <span className="font-mono-num text-lg font-semibold text-slate-900" data-testid={`property-price-${p.id}`}>
            {formatPrice(p.price)}
          </span>
          <span className="text-sm font-bold text-brand group-hover:translate-x-1 transition-transform duration-200" data-testid={`property-details-btn-${p.id}`}>
            Ver Detalhes →
          </span>
        </div>
      </div>
    </Link>
  );
}
